import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCalendarAlt, 
  faEye, 
  faEyeSlash, 
  faLock,
  faPlay,
  faPlus,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { faYoutube, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { Button, Loader } from '@mantine/core';
import { useNotification } from '../../context/NotificationContext';
import { socialMediaService } from '../../services/socialMediaService';
import { useMultiAccount } from './hooks';
import { AccountGrid } from './components';
import { multiAccountService } from './multiAccountService';
import { normalizeS3Url } from '../../utils/urlUtils';
import { 
  ProjectWithVideos, 
  SocialAccount, 
  ShareFormData,
  SocialPlatform 
} from './types';
import styles from './SocialMediaShareModal.module.css';

interface SocialMediaShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: ProjectWithVideos;
  selectedVideoUrl: string | null;
}

const SocialMediaShareModal: React.FC<SocialMediaShareModalProps> = ({
  isOpen,
  onClose,
  project,
  selectedVideoUrl
}) => {
  console.log('📺 Modal opened with selected video URL:', selectedVideoUrl);
  
  const { showNotification } = useNotification();
  const { t } = useTranslation('socialMediaShareModal');
  const {
    groupedAccounts,
    selectedAccounts,
    selectAccount,
    connectAccount,
    hasActiveAccounts,
    getSelectedAccount
  } = useMultiAccount();
  
  const [isLoading, setIsLoading] = useState(false);
  const [projectData, setProjectData] = useState(null);
  
  // Form state - platform-specific content
  const [formData, setFormData] = useState({
    youtube: {
      title: '',
      description: '',
      privacy: 'public' as 'public' | 'unlisted' | 'private',
      scheduleForLater: false,
      scheduledAt: ''
    },
    instagram: {
      title: '',
      description: '',
      privacy: 'public' as 'public' | 'unlisted' | 'private',
      scheduleForLater: false,
      scheduledAt: ''
    },
    tiktok: {
      title: '',
      description: '',
      privacy: 'public' as 'public' | 'unlisted' | 'private',
      scheduleForLater: false,
      scheduledAt: ''
    },
    selectedPlatforms: [] as SocialPlatform[]
  });

  // Platform definitions
  const [selectedContentType, setSelectedContentType] = useState<SocialPlatform>('youtube');

  const contentTypes = [
    { key: 'youtube' as SocialPlatform, name: 'YouTube', icon: faYoutube, color: '#FF0000' }
    // Disabled for beta - focus on YouTube only
    // { key: 'instagram' as SocialPlatform, name: 'Instagram', icon: faInstagram, color: '#E4405F' },
    // { key: 'tiktok' as SocialPlatform, name: 'TikTok', icon: faTiktok, color: '#000000' }
  ];

  const platforms = [
    {
      key: 'youtube' as SocialPlatform,
      name: 'YouTube',
      icon: faYoutube,
      color: '#FF0000'
    }
    // Disabled for beta - focus on YouTube only
    // {
    //   key: 'instagram' as SocialPlatform,
    //   name: 'Instagram', 
    //   icon: faInstagram,
    //   color: '#E4405F'
    // },
    // {
    //   key: 'tiktok' as SocialPlatform,
    //   name: 'TikTok',
    //   icon: faTiktok,
    //   color: '#000000'
    // }
  ];

  // Load project data when modal opens
  useEffect(() => {
    if (isOpen && project) {
      loadProjectData();
    }
  }, [isOpen, project.id]);

  const loadProjectData = async () => {
    try {
      setIsLoading(true);
      const data = await socialMediaService.getProjectData(project.id);
      setProjectData(data);
      
      // Prioritize titleData first, then suggestedContent, then other sources
      const title = 
        data.titleData?.selectedTitle ||
        data.suggestedContent?.title ||
        data.titleData?.title || 
        project.title;
      
      // Build comprehensive description with timeline and hashtags
      let description = '';
      
      // Add main description
      const mainDescription = 
        data.titleData?.generatedDescription ||
        data.suggestedContent?.description ||
        data.titleData?.description || 
        data.description || 
        project.metadata?.description || 
        `Check out this amazing ${project.type} video created with Kingdom Studio!`;
      
      description += mainDescription;
      
      // Add timeline if available
      if (data.titleData?.timeline && data.titleData.timeline.length > 0) {
        description += '\n\n⏰ Timeline:\n';
        data.titleData.timeline.forEach(item => {
          description += `${item.time} - ${item.description}\n`;
        });
      }
      
      // Add hashtags if available
      if (data.titleData?.hashtags && data.titleData.hashtags.length > 0) {
        description += '\n\n🏷️ Tags:\n';
        description += data.titleData.hashtags.join(' ') + '\n';
      } else if (data.suggestedContent?.hashtags && data.suggestedContent.hashtags.length > 0) {
        description += '\n\n🏷️ Tags:\n';
        description += data.suggestedContent.hashtags.join(' ') + '\n';
      }
      
      // Pre-populate form with project data for all platforms
      setFormData(prev => ({
        ...prev,
        youtube: {
          ...prev.youtube,
          title: title,
          description: description
        },
        instagram: {
          ...prev.instagram,
          title: title,
          description: description
        },
        tiktok: {
          ...prev.tiktok,
          title: title,
          description: description
        }
      }));
    } catch (error) {
      console.error('Error loading project data:', error);
      
      // If API call fails, use project data directly with fallback description
      const fallbackDescription = 
        project.metadata?.description || 
        project.metadata?.generatedDescription ||
        project.metadata?.titleData?.description ||
        `Check out this amazing ${project.type} video created with Kingdom Studio!`;
      
      setFormData(prev => ({
        ...prev,
        youtube: {
          ...prev.youtube,
          title: project.title,
          description: fallbackDescription
        },
        instagram: {
          ...prev.instagram,
          title: project.title,
          description: fallbackDescription
        },
        tiktok: {
          ...prev.tiktok,
          title: project.title,
          description: fallbackDescription
        }
      }));
      
      // showNotification({
      //   title: 'Info',
      //   message: 'Using project data. You can edit the description below.',
      //   type: 'info'
      // });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [selectedContentType]: {
        ...prev[selectedContentType],
        [field]: value
      }
    }));
  };

  const handleGlobalInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePlatformToggle = (platform: SocialPlatform) => {
    const hasActiveAccounts = (groupedAccounts[platform] || []).some(acc => acc.isActive && !acc.isExpired);
    
    if (!hasActiveAccounts) {
      // No active accounts - trigger connection flow
      connectAccount(platform);
      return;
    }

    // Toggle platform selection
    setFormData(prev => ({
      ...prev,
      selectedPlatforms: prev.selectedPlatforms.includes(platform)
        ? prev.selectedPlatforms.filter(p => p !== platform)
        : [...prev.selectedPlatforms, platform]
    }));
  };

  const handleAccountSelect = (platform: SocialPlatform, accountId: string) => {
    // Select the account
    selectAccount(platform, accountId);
    
    // Automatically enable the platform when an account is selected
    setFormData(prev => ({
      ...prev,
      selectedPlatforms: prev.selectedPlatforms.includes(platform)
        ? prev.selectedPlatforms
        : [...prev.selectedPlatforms, platform]
    }));
  };

  const handlePublish = async () => {
    // Validation - Check if any platforms are selected
    if (formData.selectedPlatforms.length === 0) {
      showNotification({
        title: t('notifications.selectPlatform.title'),
        message: t('notifications.selectPlatform.message'),
        type: 'error'
      });
      return;
    }

    // Validate content for each selected platform
    for (const platform of formData.selectedPlatforms) {
      const platformData = formData[platform];
      if (!platformData.title.trim()) {
        showNotification({
          title: t('notifications.titleRequired.title'),
          message: t('notifications.titleRequired.message', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }),
          type: 'error'
        });
        return;
      }

      if (platformData.scheduleForLater && !platformData.scheduledAt) {
        showNotification({
          title: t('notifications.scheduleTimeRequired.title'),
          message: t('notifications.scheduleTimeRequired.message', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }),
          type: 'error'
        });
        return;
      }
    }

    // Check for selected accounts in selected platforms
    const missingAccountPlatforms = formData.selectedPlatforms.filter(platform => {
      const selectedAccountId = selectedAccounts[platform];
      const selectedAccount = getSelectedAccount(platform);
      return !selectedAccountId || !selectedAccount;
    });

    if (missingAccountPlatforms.length > 0) {
      const platformNames = missingAccountPlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
      showNotification({
        title: t('notifications.accountSelectionRequired.title'),
        message: t('notifications.accountSelectionRequired.message', { platforms: platformNames }),
        type: 'error'
      });
      return;
    }

    // Check for expired accounts and warn user
    const expiredAccountPlatforms = formData.selectedPlatforms.filter(platform => {
      const selectedAccount = getSelectedAccount(platform);
      return selectedAccount && selectedAccount.isExpired;
    });

    if (expiredAccountPlatforms.length > 0) {
      const platformNames = expiredAccountPlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
      showNotification({
        title: t('notifications.expiredAccountWarning.title'),
        message: t('notifications.expiredAccountWarning.message', { platforms: platformNames }),
        type: 'warning'
      });
      return;
    }

    try {
      setIsLoading(true);

      // Create posts for each selected platform using platform-specific content
      for (const platform of formData.selectedPlatforms) {
        const selectedAccount = getSelectedAccount(platform);
        if (!selectedAccount) continue;

        const platformData = formData[platform];
        const shareData: ShareFormData = {
          projectId: project.id,
          socialAccountId: selectedAccount.id,
          platform: platform,
          title: platformData.title,
          description: platformData.description,
          thumbnailUrl: project.thumbnail,
          privacy: platformData.privacy,
          scheduledAt: platformData.scheduleForLater ? platformData.scheduledAt : undefined,
          scheduleForLater: platformData.scheduleForLater
        };

        await socialMediaService.createSocialPost(shareData);
      }

      const isScheduled = formData.selectedPlatforms.some(p => formData[p].scheduleForLater);
      showNotification({
        title: t('notifications.success.title'),
        message: isScheduled ? t('notifications.success.messageScheduled') : t('notifications.success.messagePublished'),
        type: 'success'
      });

      onClose();
    } catch (error) {
      console.error('Error publishing content:', error);
      showNotification({
        title: t('notifications.publishingFailed.title'),
        message: t('notifications.publishingFailed.message'),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Use the selected video URL passed from the card and normalize it
  const rawVideoUrl = selectedVideoUrl || project.originalVideoUrl || project.generatedVideoUrl;
  const videoUrl = normalizeS3Url(rawVideoUrl);
  
  console.log('📺 Modal opened - Raw URL:', selectedVideoUrl);
  console.log('🎥 Modal using - Normalized URL:', videoUrl);
  
  // Determine which version is being used for display
  // Compare normalized versions for accurate matching
  const getVideoVersionLabel = () => {
    const normalizedSelected = normalizeS3Url(selectedVideoUrl);
    // Check both direct property and metadata path for soundtrack
    const soundtrackUrl = project.soundTrackVideoUrl || project.metadata?.soundtrack?.soundtrackVideoUrl;
    
    if (normalizedSelected === normalizeS3Url(soundtrackUrl)) return t('projectPreview.versions.soundtrack');
    if (normalizedSelected === normalizeS3Url(project.captionedVideoUrl)) return t('projectPreview.versions.captioned');
    if (normalizedSelected === normalizeS3Url(project.generatedVideoUrl)) return t('projectPreview.versions.generated');
    if (normalizedSelected === normalizeS3Url(project.originalVideoUrl)) return t('projectPreview.versions.original');
    return t('projectPreview.versions.unknown');
  };
  
  const videoVersionLabel = getVideoVersionLabel();

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{t('title')}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <Loader size="lg" />
              <p>{t('notifications.loadingProject')}</p>
            </div>
          ) : (
            <>
              {/* Project Preview */}
              <div className={styles.projectPreview}>
               
                <div className={styles.videoPreview}>
                  {videoUrl ? (
                    <video
                      src={videoUrl}
                      poster={project.thumbnail}
                      controls
                      className={styles.previewVideo}
                    />
                  ) : (
                    <div className={styles.placeholderVideo}>
                      <FontAwesomeIcon icon={faPlay} size="2x" />
                      <p>{t('projectPreview.noVideo')}</p>
                    </div>
                  )}
                </div>
                <div className={styles.projectInfo}>
                  <h3>{project.title}</h3>
                  <p>{t('projectPreview.duration', { duration: project.duration })}</p>
                  <p>{t('projectPreview.using', { version: videoVersionLabel })}</p>
                </div>
              </div>

              {/* Content Form */}
              <div className={styles.contentForm}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>{t('form.title.label')} {t('form.title.required')}</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={formData[selectedContentType].title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder={t('form.title.placeholder', { platform: selectedContentType })}
                    maxLength={100}
                  />
                  <span className={styles.charCount}>{t('form.title.charCount', { count: formData[selectedContentType].title.length })}</span>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>{t('form.description.label')}</label>
                  <textarea
                    className={styles.textarea}
                    value={formData[selectedContentType].description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder={t('form.description.placeholder', { platform: selectedContentType })}
                    rows={4}
                    maxLength={5000}
                  />
                  <span className={styles.charCount}>{t('form.description.charCount', { count: formData[selectedContentType].description.length })}</span>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t('form.privacy.label')}</label>
                    <div className={styles.radioGroup}>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="privacy"
                          value="public"
                          checked={formData[selectedContentType].privacy === 'public'}
                          onChange={(e) => handleInputChange('privacy', e.target.value)}
                        />
                        <FontAwesomeIcon icon={faEye} />
                        {t('form.privacy.public')}
                      </label>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="privacy"
                          value="unlisted"
                          checked={formData[selectedContentType].privacy === 'unlisted'}
                          onChange={(e) => handleInputChange('privacy', e.target.value)}
                        />
                        <FontAwesomeIcon icon={faEyeSlash} />
                        {t('form.privacy.unlisted')}
                      </label>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="privacy"
                          value="private"
                          checked={formData[selectedContentType].privacy === 'private'}
                          onChange={(e) => handleInputChange('privacy', e.target.value)}
                        />
                        <FontAwesomeIcon icon={faLock} />
                        {t('form.privacy.private')}
                      </label>
                    </div>
                  </div>
                </div>

                <div className={styles.scheduleSection}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData[selectedContentType].scheduleForLater}
                      onChange={(e) => handleInputChange('scheduleForLater', e.target.checked)}
                    />
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {t('form.schedule.label')}
                  </label>
                  
                  {formData[selectedContentType].scheduleForLater && (
                    <input
                      type="datetime-local"
                      className={styles.dateInput}
                      value={formData[selectedContentType].scheduledAt}
                      onChange={(e) => handleInputChange('scheduledAt', e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  )}
                </div>

                {/* Platform Selection */}
                <div className={styles.platformSection}>
                  <div className={styles.sectionHeader}>
                    <label className={styles.label}>{t('platforms.shareTo')}</label>
                    
                    {/* Content Type Toggle Buttons */}
                    <div className={styles.contentTypeToggle}>
                      {contentTypes.map(contentType => (
                        <button
                          key={contentType.key}
                          className={`${styles.contentTypeButton} ${selectedContentType === contentType.key ? styles.active : ''}`}
                          onClick={() => setSelectedContentType(contentType.key)}
                        >
                          <FontAwesomeIcon 
                            icon={contentType.icon} 
                            style={{ color: contentType.color }}
                          />
                          <span>{t(`platforms.${contentType.key}`)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Compact Platform Tabs - Show only selected content type */}
                  <div className={styles.platformTabs}>
                    {platforms.filter(platform => platform.key === selectedContentType).map(platform => {
                      const accounts = groupedAccounts[platform.key] || [];
                      const activeAccounts = accounts.filter(acc => acc.isActive);
                      const hasAccounts = activeAccounts.length > 0;
                      const isPlatformSelected = formData.selectedPlatforms.includes(platform.key);
                      const selectedAccountId = selectedAccounts[platform.key];
                      
                      return (
                        <div
                          key={platform.key}
                          className={`${styles.platformTab} ${isPlatformSelected ? styles.selected : ''} ${hasAccounts ? styles.hasAccounts : ''}`}
                        >
                          {/* Platform Header - No separate checkbox */}
                          <div className={styles.platformHeader}>
                            <div className={styles.platformInfo}>
                              <FontAwesomeIcon 
                                icon={platform.icon} 
                                style={{ color: platform.color }}
                                className={styles.platformIcon}
                              />
                              <span className={styles.platformName}>{t(`platforms.${platform.key}`)}</span>
                              {hasAccounts && (
                                <span className={styles.accountCount}>{t('platforms.accountCount', { count: activeAccounts.length })}</span>
                              )}
                            </div>
                            {/* Show selection indicator when platform is selected */}
                            {isPlatformSelected && (
                              <div className={styles.selectedIndicator}>
                                <FontAwesomeIcon icon={faCheck} />
                              </div>
                            )}
                          </div>
                          
                          {/* Horizontal Account Row */}
                          {hasAccounts ? (
                            <div className={styles.accountRow}>
                              {activeAccounts.map((account) => (
                                <div
                                  key={account.id}
                                  className={`${styles.accountChip} ${selectedAccountId === account.id ? styles.selectedAccount : ''} ${account.isExpired ? styles.expiredAccount : ''}`}
                                  onClick={async () => {
                                    if (account.isExpired) {
                                      // Try token refresh first
                                      try {
                                        const refreshSuccess = await multiAccountService.refreshAccountToken(account.id);
                                        if (refreshSuccess) {
                                          showNotification({
                                            title: t('notifications.tokenRefreshed.title'),
                                            message: t('notifications.tokenRefreshed.message', { accountName: account.displayName || account.platformUsername }),
                                            type: 'success'
                                          });
                                          // Reload accounts to show updated status
                                          window.location.reload(); // Simple refresh for now
                                        } else {
                                          // If refresh fails, trigger full reconnection
                                          connectAccount(platform.key);
                                        }
                                      } catch (error) {
                                        console.error('Token refresh failed:', error);
                                        // Fallback to full reconnection
                                        connectAccount(platform.key);
                                      }
                                    } else {
                                      // Select account normally
                                      handleAccountSelect(platform.key, account.id);
                                    }
                                  }}
                                  title={account.isExpired ? 'Click to refresh expired token' : 'Select this account'}
                                >
                                  <div className={styles.accountAvatar}>
                                    {account.profileImageUrl ? (
                                      <img src={account.profileImageUrl} alt={account.displayName || account.platformUsername} />
                                    ) : (
                                      <FontAwesomeIcon icon={platform.icon} />
                                    )}
                                    {account.isExpired && <div className={styles.expiredBadge}>!</div>}
                                  </div>
                                  <span className={styles.accountName}>
                                    {account.displayName || account.platformUsername || 'Account'}
                                  </span>
                                  {account.isExpired && <span className={styles.expiredText}>{t('accounts.expired')}</span>}
                                </div>
                              ))}
                              
                              {/* Add Account Button */}
                              <div 
                                className={styles.addAccountChip}
                                onClick={() => connectAccount(platform.key)}
                              >
                                <div className={styles.addIcon}>
                                  <FontAwesomeIcon icon={faPlus} />
                                </div>
                                <span>{t('platforms.add')}</span>
                              </div>
                            </div>
                          ) : (
                            <div className={styles.noAccountsRow}>
                              <button 
                                className={styles.connectButton}
                                onClick={() => connectAccount(platform.key)}
                              >
                                <FontAwesomeIcon icon={faPlus} />
                                {t('platforms.connectToShare')}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={isLoading}
            className={styles.cancelButton}
          >
            {t('actions.cancel')}
          </Button>
          <Button 
            onClick={handlePublish} 
            loading={isLoading}
            disabled={formData.selectedPlatforms.length === 0}
            className={styles.publishButton}
          >
            {formData.selectedPlatforms.some(p => formData[p].scheduleForLater) ? t('actions.schedule') : t('actions.publish')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaShareModal;
